import { IdentityHttpError, type IdentityHttpErrorKind } from './identity-http';

interface RefusalEnvelopePolicy {
  readonly requireClientStatus?: boolean;
  readonly excludedKinds?: readonly IdentityHttpErrorKind[];
}

export type ValidatedRefusalError = IdentityHttpError & { canonical: NonNullable<IdentityHttpError['canonical']> };

/** IdentityHttpClient is the only wire validator. This helper accepts only its
 * validated envelope, under the caller's existing admission policy. */
function requireRefusalEnvelope(error: unknown, policy: RefusalEnvelopePolicy): asserts error is ValidatedRefusalError {
  if (!(error instanceof IdentityHttpError) || error.canonical === null
    || (policy.requireClientStatus && (error.status === null || error.status >= 500))
    || policy.excludedKinds?.includes(error.kind)) throw error;
}

export type RefusalStatusReason = 'authorization_denied' | 'conflict' | 'not_found' | 'invalid_request' | 'unknown';
type RefusalRetry = 'after_permission_change' | 'after_refresh' | 'manual_only';

/**
 * I-22 · the one status table every Panel screen maps a validated refusal with.
 * 409 is a state conflict, 412 a stale If-Match, and 428 an If-Match the owner
 * could not use (missing, or below its minimum version). All three mean the
 * version this screen read is not the one to write against, so all three are a
 * `conflict` that a re-read resolves.
 * @internal
 */
export function refusalStatusReason(status: number | null): RefusalStatusReason {
  return status === 401 || status === 403 ? 'authorization_denied'
    : status === 409 || status === 412 || status === 428 ? 'conflict'
      : status === 404 ? 'not_found'
        : status === 400 || status === 422 ? 'invalid_request' : 'unknown';
}

/** `refreshReasons` names a screen's own reasons that a re-read resolves, like `conflict`.
 * @internal */
export function refusalRetry(reason: string, refreshReasons: readonly string[] = []): RefusalRetry {
  return reason === 'authorization_denied' ? 'after_permission_change'
    : reason === 'conflict' || refreshReasons.includes(reason) ? 'after_refresh' : 'manual_only';
}

/**
 * A screen's refusal policy: which validated envelopes it admits, and its own
 * reason table on top of the shared status reason. `classify` returns null to
 * keep the status reason.
 */
export interface CanonicalRefusalPolicy<Reason extends string = never> extends RefusalEnvelopePolicy {
  readonly classify?: (error: ValidatedRefusalError, statusReason: RefusalStatusReason) => Reason | null;
  readonly refreshReasons?: readonly Reason[];
}

export type MappedCanonicalRefusal<Reason extends string = never> = Readonly<{
  kind: 'canonical_refusal';
  reason: Reason | RefusalStatusReason;
  error: ValidatedRefusalError['canonical'];
  retry: RefusalRetry;
}>;

/**
 * I-22 · the one refusal mapper of every Panel live port. Anything outside the
 * policy (a network failure, a server outage, an unvalidated response) is
 * rethrown unchanged: it never proves whether a mutation committed.
 */
export function canonicalRefusal<Reason extends string = never>(
  error: unknown,
  policy: CanonicalRefusalPolicy<Reason>,
): MappedCanonicalRefusal<Reason> {
  requireRefusalEnvelope(error, policy);
  const statusReason = refusalStatusReason(error.status);
  const reason = policy.classify?.(error, statusReason) ?? statusReason;
  return { kind: 'canonical_refusal', reason, error: error.canonical, retry: refusalRetry(reason, policy.refreshReasons) };
}

/** For result vocabularies that carry no retry hint. */
export function withoutRetry<Refusal extends Readonly<{ retry: RefusalRetry }>>({ retry: _retry, ...refusal }: Refusal): Omit<Refusal, 'retry'> {
  return refusal;
}

/** I-19 · the HTTP status a refusal envelope carries, for the support note. */
export function refusalEnvelopeStatus(envelope: unknown): number | null {
  return typeof envelope === 'object' && envelope !== null && 'statusCode' in envelope
    && typeof envelope.statusCode === 'number' ? envelope.statusCode : null;
}

/**
 * `docs/decisions/2026-09-23-server-actions.md`, decision 5: a
 * staff refusal of the Service carries the catalogue's plain Spanish sentence
 * (`reason_text`) in its `errors` envelope, with any amount it names already
 * filled in from the refusal's details. A screen shows that sentence as it comes.
 */
export function refusalEnvelopeReasonText(envelope: unknown): string | null {
  const errors = typeof envelope === 'object' && envelope !== null && 'errors' in envelope ? envelope.errors : null;
  const text = typeof errors === 'object' && errors !== null && 'reason_text' in errors ? errors.reason_text : null;
  return typeof text === 'string' && text.trim().length > 0 && text.length <= 500 ? text.trim() : null;
}

/**
 * I-19 · a screen's own sentence for a canonical code: its exact codes first,
 * then its `family:detail` prefixes. Screens never print the server's sentence.
 */
export function namedRefusalMessage(
  code: string | null,
  messages: Readonly<Record<string, string>>,
  prefixes: readonly (readonly [prefix: string, message: string])[] = [],
): string | undefined {
  if (code === null) return undefined;
  if (Object.prototype.hasOwnProperty.call(messages, code)) return messages[code];
  return prefixes.find(([prefix]) => code.startsWith(prefix) && code.length > prefix.length)?.[1];
}

/** The shared try/catch of every live port: a success, or the port's own refusal.
 * Whatever `refuse` rethrows stays a rejected request. */
export async function settleRefusal<Canonical, Refusal>(
  request: () => Promise<Canonical>,
  refuse: (error: unknown) => Refusal,
): Promise<Readonly<{ kind: 'success'; canonical: Canonical }> | Refusal> {
  try { return { kind: 'success', canonical: await request() }; }
  catch (error) { return refuse(error); }
}
